import React, { useCallback, useEffect, useRef, useState } from "react";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Cropper } from "react-cropper";
import { useDispatch, useSelector } from "react-redux";
import { isValidPhoneNumber } from "react-phone-number-input";

import DeleteIcon from "@mui/icons-material/Delete";
import FormControl from "@mui/material/FormControl";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import { Loader } from "../../common/loader";
import DialogBox from "../../common/DialogBox";
import FileUpload from "../../common/FileUpload";
import { getObjLength } from "../../helpers/utils";
import CustomTextField from "../../common/CustomTextField";
import CommonAgentsAdminTable from "./CommonAgentsAdminTable";
import PlainAlertMessage from "../../common/PlainAlertMessage";
import CustomPhoneNumberInput from "../../common/CustomPhoneNumberInput";
import { MenuProps, PER_PAGE, USA_STATES } from "../../helpers/constants";
import add_photoIcon from "../../../assets/dashboard/add_photo_alternate.png";
import { closeDeleteIconStyle } from "../../admin/providers/CreateProvidersForm";
import { agentsAdminAction } from "../../../redux/slices/brokers/agentAdminSlice";

import "../BrokerSignupPage.scss";

const columns = [
  {
    key: 1,
    label: "",
    id: "action",
    minWidth: 10,
  },
  {
    key: 2,
    label: "Office",
    id: "office_name",
    width: 120,
  },
  {
    key: 3,
    label: "Address",
    id: "address",
    // maxWidth: 80,
  },
  {
    key: 4,
    label: "City",
    id: "city",
    minWidth: 30,
  },
  {
    key: 5,
    label: "State",
    id: "state",
    minWidth: 30,
  },
  {
    key: 6,
    label: "Zip",
    id: "zip",
    minWidth: 50,
  },
  {
    key: 7,
    label: "Phone number",
    id: "phone",
    width: 180,
  },
  {
    key: 8,
    label: "Website",
    id: "website",
    // width: 150,
    copyIcon: true,
    alignProp: "left",
  },
];

const agentOfficesValidationSchema = Yup.object().shape({
  office_name: Yup.string("Please enter your First Name.")
    .trim("The First name cannot include leading and trailing spaces")
    .strict(true)
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("First Name is required"),

  address: Yup.string("Please enter your Address.")
    .trim("The Address cannot include leading and trailing spaces")
    .strict(true)
    .min(5, "Too Short!")
    .max(100, "Too Long!")
    .required("Address is required"),

  city: Yup.string("Please enter your City.")
    .trim("The city cannot include leading and trailing spaces")
    .strict(true)
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("City is required"),
  state: Yup.string("Please select the state").required("State is required"),

  zip: Yup.string("Please enter your Zip code.")
    .required("Zip is required")
    .matches(/^[0-9]{5}$/, "Zipcode must be exactly 5 digits"),
  // .min(6, "Zip needs 6 digits")
  // .max(6, "Zip needs 6 digits"),

  email: Yup.string("Please enter your Email Id.")
    .email("Invalid email format")
    .max(255),
  //.required("Email id is required"),

  phone: Yup.string("Please enter your Phone number.").typeError(
    "That doesn't look like a phone number"
  ),
  // .positive()
  //.required("Phone number is required"),
  // fax: Yup.string()
  //   .typeError("That doesn't look like a fax number")
  //   .matches(
  //     /^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
  //     "Enter a valid fax number"
  //   ),
  website: Yup.string().matches(
    /^(https?:\/\/)?([^\s$.?#]+\.)+[a-zA-Z]{2,}(\/[^\s]*)*$/i,
    "Invalid URL pattern"
  ),
});

const AgentOffices = ({
  base_route = "broker",
  isEditOfficeModal = false,
  officeData = {},
  setIsEditOfficeModal = () => {},
  isSuperAdmin = false,
  broker_data = {},
}) => {
  const dispatch = useDispatch();

  const signUpFormRef = useRef();
  const profileDropzoneRef = useRef();
  const cropperProfileRef = useRef();

  const [croppedProfileImage, setCroppedProfileImage] = useState(null);
  const [selectedProfileFile, setSelectedProfileFile] = useState(null);
  const [profileLoader, setProfileLoader] = useState(false); // State for loader
  const [agentOfficeModalOpen, setAgentOfficeModalOpen] = useState(false);
  const [isEditAgentOffice, setIsEditAgentOffice] = useState(isEditOfficeModal);
  const [selectedProperty, setSelectedProperty] = useState({});

  const [payload, setPayload] = useState({
    limit: 10,
    offset: 0,
  });
  const [pageCount, setPageCount] = useState(0);

  const {
    agentAdminLoader,
    createAgentOfficeDetails,
    agentOfficeDetails,
    isAddBtnClicked,
  } = useSelector((state) => state?.agentsAdmin);
  const { brokerDetails } = useSelector((state) => state?.brokerAgents);

  const openAddOfficeModal = () => {
    setSelectedProperty();
    setAgentOfficeModalOpen(true);
  };
  const closeAddOfficeModal = () => {
    setAgentOfficeModalOpen(false);
    setIsEditOfficeModal(false);
    setSelectedProperty();
    setIsEditAgentOffice(false);
    setCroppedProfileImage();
    dispatch(agentsAdminAction.fetchCreateAgentOfficeData({}));
    dispatch(agentsAdminAction.fetchIsAddBtnClicked(false));
  };

  const handleEditOfficeModal = () => {
    setAgentOfficeModalOpen(true);
    setIsEditAgentOffice(true);
    dispatch(agentsAdminAction.fetchCreateAgentOfficeData({}));
  };

  // when user select the image
  const onFileChange = (files, type) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCroppedProfileImage(null);
      setSelectedProfileFile(reader.result);
    };
    reader.readAsDataURL(files[0]);
  };
  // get cropped image
  const getCropData = (filedName) => {
    // Start loading

    setProfileLoader(true);

    setTimeout(() => {
      // Simulate processing delay

      const croppedData = cropperProfileRef.current?.cropper
        .getCroppedCanvas()
        .toDataURL();
      setCroppedProfileImage(croppedData);
      setSelectedProfileFile(null);
      signUpFormRef?.current?.setFieldValue(filedName, croppedData);

      // Stop loading after cropping is done
      setProfileLoader(false);
    }, 500); // Simulated processing delay (adjust if needed)
  };

  const triggerFileUpload = (type = "logo") => {
    setTimeout(() => {
      setSelectedProfileFile(null);
    }, 500);
    const fileInput =
      profileDropzoneRef.current?.querySelector('input[type="file"]');
    if (fileInput) fileInput.click();
  };

  const customFileUploadInnerSection = (getInputProps, getRootProps) => {
    return (
      <div
        className="dz-message needsclick custom-dropzone"
        style={{ padding: "16px" }}
        {...getRootProps()}
      >
        <div
          className="dz-message needsclick custom-dropzone"
          style={{ padding: "16px" }}
        >
          <input {...getInputProps()} name="file" />
          <i className="d-inline-block">
            <img src={add_photoIcon} alt="add_photoIcon" />
          </i>
        </div>
      </div>
    );
  };

  const onSubmit = (values) => {
    if (values?.logo === selectedProperty?.logo) {
      delete values?.logo;
    }

    let data = { ...values, broker_id: brokerDetails?.data?.broker?.id };
    if (isSuperAdmin) {
      data = { ...data, from_super_admin: true, broker_id: broker_data?.id };
    }
    let url = `broker/create-office?source=${base_route}`;
    if (isEditAgentOffice) {
      url = `broker/update-office/${selectedProperty?.id}?source=${base_route}`;
    }
    dispatch(
      agentsAdminAction.createAgentOfficeDetails({
        url,
        data,
        brokertoken: !isSuperAdmin,
        token: isSuperAdmin,
      })
    );
  };

  const agentProfileAPICall = () => {
    setIsEditOfficeModal(false);
    if (!isSuperAdmin) {
      dispatch(
        agentsAdminAction.getAdminProfileData({
          url: `profile?source=${base_route}`,
          data: {},
          brokertoken: true,
          source: base_route,
        })
      );
      window.scrollTo(0, 0);
    }
  };

  const agentOfficesListAPICall = useCallback(() => {
    let data = {
      limit: payload?.limit,
      offset: payload?.offset,
      broker_id: brokerDetails?.data?.broker?.id,
    };
    if (isSuperAdmin) {
      data = { ...data, from_super_admin: true, broker_id: broker_data?.id };
    }
    dispatch(
      agentsAdminAction.getAgentOfficeDetails({
        url: `broker/get-offices?source=${base_route}`,
        data,
        brokertoken: !isSuperAdmin,
        token: isSuperAdmin,
      })
    );
  }, [dispatch, payload?.limit, payload?.offset]);

  useEffect(() => {
    if (!isEditOfficeModal) {
      agentOfficesListAPICall();
    }
  }, [agentOfficesListAPICall]);

  useEffect(() => {
    if (
      getObjLength(createAgentOfficeDetails) &&
      createAgentOfficeDetails?.success
    ) {
      agentOfficesListAPICall();
    }
  }, [createAgentOfficeDetails]);

  useEffect(() => {
    if (getObjLength(agentOfficeDetails)) {
      if (agentOfficeDetails?.success) {
        setPageCount(Math.ceil(agentOfficeDetails?.count / PER_PAGE));
        if (agentOfficeDetails?.count === 10) {
          setPayload((prevState) => ({
            ...prevState,
            offset: 0,
          }));
        }
      }
    }
  }, [agentOfficeDetails]);

  useEffect(() => {
    if (isEditAgentOffice) {
      setCroppedProfileImage(selectedProperty?.logo);
    } else {
      setCroppedProfileImage(null);
    }
  }, [isEditAgentOffice]);

  useEffect(() => {
    if (isEditOfficeModal) {
      setSelectedProperty(officeData);
      handleEditOfficeModal();
      setCroppedProfileImage(officeData?.logo);
    }
  }, [isEditOfficeModal]);

  useEffect(() => {
    if (isSuperAdmin) {
      columns.forEach((col) => {
        if (col.key === 1) {
          col.id = "sr";
          col.label = "No.";
        }
      });
      if (!columns.find((e) => e.key === 9)) {
        columns.push({
          key: 9,
          label: "Action",
          id: "action",
          minWidth: 10,
        });
      }

      columns.sort(function (a, b) {
        return a.key - b.key;
      });
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isAddBtnClicked) {
      openAddOfficeModal();
    }
  }, [isAddBtnClicked]);

  function addOfficesForm(isEdit = isEditAgentOffice) {
    return (
      <div className="broker-create-account agent_admin_form_modal">
        <Box className="create_acc_form_wrapper mt-0">
          <Box className="box-container agents_form agent_admin_form_modal">
            <Formik
              innerRef={signUpFormRef}
              enableReinitialize
              validateOnChange
              initialValues={{
                logo: selectedProperty?.logo || "",
                office_name: selectedProperty?.office_name || "",
                address: selectedProperty?.address || "",
                city: selectedProperty?.city || "",
                state: selectedProperty?.state || "",
                zip: selectedProperty?.zip || "",
                email: selectedProperty?.email || "",
                phone: selectedProperty?.phone || "",
                fax: selectedProperty?.fax || "",
                website: selectedProperty?.website || "",
              }}
              validationSchema={agentOfficesValidationSchema}
              onSubmit={(values) => {
                if (values?.phone && !isValidPhoneNumber(values?.phone)) {
                  return;
                }
                onSubmit(values);
              }}
            >
              {({ values, setFieldValue, touched, errors, initialValues }) => {
                return (
                  <Form>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={3}>
                        <Grid item xs={12} md={12}>
                          <InputLabel shrink htmlFor="bootstrap-input">
                            Logo
                          </InputLabel>
                          {/* When cropper will crop the image then we will have data in the croppedProfileImage state so code will transfer to else condition */}
                          {!croppedProfileImage ? (
                            selectedProfileFile ? (
                              <>
                                {/* When select the image then this cropper will be displayed */}
                                <Cropper
                                  ref={cropperProfileRef}
                                  style={{ height: "150px", width: "150px" }}
                                  zoomTo={0}
                                  initialAspectRatio={1}
                                  src={selectedProfileFile}
                                  viewMode={1}
                                  minCropBoxHeight={10}
                                  minCropBoxWidth={10}
                                  background={false}
                                  responsive={true}
                                  autoCropArea={1}
                                  checkOrientation={false}
                                  guides={false}
                                />
                                <Box
                                  className="d-flex justify-content-start"
                                  mt={2}
                                >
                                  <Button
                                    onClick={() => getCropData("logo")}
                                    variant="contained"
                                    className="success-btn"
                                    style={{ padding: "6px 16px" }}
                                    // color="primary"
                                  >
                                    {profileLoader ? (
                                      <>
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                        &nbsp;&nbsp;Processing..
                                      </>
                                    ) : (
                                      `Crop Logo`
                                    )}
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <>
                                {/* Initially there is neither cropped image nor selected image so upload image field will show */}
                                <span className="servicer-signup-form">
                                  <FileUpload
                                    name="upload_doc"
                                    accept={{
                                      "image/png": [".png", ".jpeg", ".jpg"],
                                    }}
                                    multiple={false}
                                    onFileUpload={async (file) => {
                                      onFileChange(file, "logo");
                                    }}
                                    maxSize={10}
                                    dragDropText={`Drag and drop Logo or `}
                                    greenText="Upload a Logo"
                                    subText="Only .png, .jpeg, .jpg files are allowed"
                                    customFileUploadInnerSection={
                                      customFileUploadInnerSection
                                    }
                                  />
                                </span>
                              </>
                            )
                          ) : (
                            <>
                              {/* After successfully cropping it will display the cropped image and delete icon for clearing the image */}
                              <Box
                                className="position-relative p-4 rounded d-flex justify-content-center align-items-center"
                                sx={{
                                  backgroundColor: "#F6F9FC",
                                  border: croppedProfileImage
                                    ? "1px solid #E2E8EB"
                                    : "",
                                  width: "150px",
                                  height: "150px",
                                }}
                              >
                                <img
                                  height="150px"
                                  width="150px"
                                  src={croppedProfileImage}
                                  // style={{
                                  //   minHeight: "150px",
                                  //   maxHeight: "150px",
                                  // }}
                                  alt="cropped"
                                  id="croppedProfileImage"
                                  className="rounded"
                                />

                                <IconButton
                                  sx={{
                                    ...closeDeleteIconStyle,
                                    color: "#FC2125",
                                    fontSize: "1.4rem",
                                  }}
                                  onClick={() => {
                                    setCroppedProfileImage(null);
                                    setSelectedProfileFile(null);
                                    signUpFormRef?.current?.setFieldValue(
                                      "logo",
                                      ""
                                    );
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </>
                          )}
                          {/* //  {isSubmit && showLogoError === 0 && ( */}
                          {touched?.logo && Boolean(errors?.logo) && (
                            <FormHelperText
                              sx={{
                                color: "#d32f2f !important",
                                m: "0 !important",
                                fontSize: "12px !important",
                              }}
                            >
                              Please add your Logo
                            </FormHelperText>
                          )}
                          {isEdit ? (
                            <>
                              {!selectedProfileFile && (
                                <Button
                                  variant="outlined"
                                  className="mt-2 text-capitalize"
                                  onClick={() => triggerFileUpload("logo")}
                                >
                                  Edit&nbsp;
                                  <EditOutlinedIcon fontSize="small" />
                                </Button>
                              )}

                              {/* Dropzone mounted, just hidden if image exists */}
                              <div
                                ref={profileDropzoneRef}
                                style={{ display: "none" }}
                              >
                                <FileUpload
                                  name="upload_doc"
                                  accept={{
                                    "image/png": [".png", ".jpeg", ".jpg"],
                                  }}
                                  multiple={false}
                                  onFileUpload={(file) =>
                                    onFileChange(file, "logo")
                                  }
                                  onFileDialogCancel={() => {
                                    setSelectedProfileFile(null);
                                  }}
                                  maxSize={10}
                                />
                              </div>
                            </>
                          ) : (
                            <p className="files_texts">
                              Click Here to Upload your Logo that will appear on
                              all emails.
                            </p>
                          )}
                          <FormHelperText>
                            {touched.upload_doc && errors.upload_doc && (
                              <Typography
                                className="text-danger"
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {errors.upload_doc}
                              </Typography>
                            )}
                          </FormHelperText>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={9}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Office name
                            </InputLabel>
                            <CustomTextField
                              name="office_name"
                              validation={{
                                values,
                                setFieldValue,
                                touched,
                                errors,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Address
                            </InputLabel>
                            <CustomTextField
                              name="address"
                              validation={{
                                values,
                                setFieldValue,
                                touched,
                                errors,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              City
                            </InputLabel>
                            <CustomTextField
                              name="city"
                              validation={{
                                values,
                                setFieldValue,
                                touched,
                                errors,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              State
                            </InputLabel>
                            <FormControl
                              fullWidth
                              className="sign-textField text-center"
                              error={touched.state && Boolean(errors.state)}
                            >
                              <Select
                                className="text-start"
                                labelId="demo-simple-select-placeholder"
                                id="demo-simple-select"
                                value={values?.state}
                                name="state"
                                variant="outlined"
                                onChange={(event) => {
                                  setFieldValue("state", event.target.value);
                                }}
                                MenuProps={MenuProps}
                                displayEmpty
                              >
                                <MenuItem disabled value="">
                                  <span style={{ color: "#00000075" }}>
                                    State
                                  </span>
                                </MenuItem>
                                {USA_STATES?.map((state) => (
                                  <MenuItem value={state?.code}>
                                    {state?.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {touched.state && Boolean(errors.state) && (
                                <FormHelperText sx={{ color: "#d32f2f" }}>
                                  {touched.state && errors.state}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Zip
                            </InputLabel>
                            <CustomTextField
                              name="zip"
                              validation={{
                                values,
                                setFieldValue,
                                touched,
                                errors,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Email
                            </InputLabel>
                            <CustomTextField
                              name="email"
                              validation={{
                                values,
                                setFieldValue,
                                touched,
                                errors,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Office number
                            </InputLabel>
                            <CustomPhoneNumberInput
                              name="phone"
                              placeholder={null}
                              values={values}
                              touched={touched}
                              setFieldValue={setFieldValue}
                              errors={errors}
                              otherProps={{
                                onChange: (event) => {
                                  setFieldValue("phone", event);
                                  if (!event) {
                                    setFieldValue("phone", "");
                                  }
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Fax Number
                            </InputLabel>
                            <CustomPhoneNumberInput
                              name="fax"
                              placeholder={null}
                              values={values}
                              touched={touched}
                              setFieldValue={setFieldValue}
                              errors={errors}
                              isFaxNumber={true}
                              classes="hide_flags"
                              isdefaultCountryUS={true}
                              isValidation={false}
                              otherProps={{
                                onChange: (event) => {
                                  setFieldValue("fax", event);
                                  if (!event) {
                                    setFieldValue("fax", "");
                                  }
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} px={1} pb={1}>
                            <InputLabel shrink htmlFor="bootstrap-input">
                              Website
                            </InputLabel>
                            <CustomTextField
                              name="website"
                              validation={{
                                values,
                                setFieldValue,
                                touched,
                                errors,
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {getObjLength(createAgentOfficeDetails) &&
                      !createAgentOfficeDetails?.success && (
                        <p className="fw-500 terms_error w-100 text-danger text-center">
                          {createAgentOfficeDetails?.data?.message}
                        </p>
                      )}
                    <Grid
                      item
                      xs={12}
                      md={12}
                      px={1}
                      className="d-flex justify-content-between align-items-center mt-3"
                    >
                      <Button
                        onClick={closeAddOfficeModal}
                        className="popup_Btn other_popup_btn"
                        variant="contained"
                      >
                        Discard
                      </Button>

                      <Button
                        type="submit"
                        variant="contained"
                        className={`popup_Btn success_popup_btn  ${
                          JSON.stringify(initialValues) ===
                            JSON.stringify(values) && "cursor-disable"
                        }`}
                        disabled={
                          JSON.stringify(initialValues) ===
                          JSON.stringify(values)
                        }
                      >
                        Save Changes
                      </Button>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div>
      {!isEditOfficeModal && (
        <CommonAgentsAdminTable
          columns={columns}
          rowData={agentOfficeDetails?.success ? agentOfficeDetails?.data : []}
          btn_texts="Add Office"
          addBtnClick={openAddOfficeModal}
          pageCount={pageCount}
          setPageCount={setPageCount}
          payload={payload}
          setPayload={setPayload}
          setEditRecord={handleEditOfficeModal}
          setSelectedProperty={setSelectedProperty}
          selectedPropertyID={selectedProperty?.id}
          deleteAPIURL={"broker/delete-office"}
          customeDeleteMessage="Are you sure you want to delete this Office? This action can not be undone."
          listAPICall={() => {
            agentOfficesListAPICall();
            agentProfileAPICall();
          }}
          base_route={base_route}
          isSuperAdmin={isSuperAdmin}
        />
      )}
      {/* Add Office form modal */}
      {agentOfficeModalOpen && (
        <DialogBox
          dividers
          width={
            getObjLength(createAgentOfficeDetails) &&
            createAgentOfficeDetails?.success
              ? "sm"
              : "lg"
          }
          title={
            (!getObjLength(createAgentOfficeDetails) ||
              !createAgentOfficeDetails?.success) &&
            `${isEditAgentOffice ? "Edit" : "Add"} Office`
          }
          contentClassName={"bg-white p-0"}
          openPopup={agentOfficeModalOpen}
          setOpenPopup={setAgentOfficeModalOpen}
          onClose={
            getObjLength(createAgentOfficeDetails) &&
            createAgentOfficeDetails?.success
              ? false
              : closeAddOfficeModal
          }
          content={
            !getObjLength(createAgentOfficeDetails) ||
            !createAgentOfficeDetails?.success ? (
              <>{addOfficesForm()}</>
            ) : (
              <PlainAlertMessage
                type="Success"
                openPopup={true}
                onClose={true}
                onCloseFun={() => {
                  agentProfileAPICall();
                  agentOfficesListAPICall();
                  closeAddOfficeModal();
                }}
                message={createAgentOfficeDetails?.message}
                footer={false}
              />
            )
          }
        />
      )}
      <Loader open={agentAdminLoader} />
    </div>
  );
};

export default AgentOffices;
